import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Lock, CalendarPlus, Video, MapPin, Users } from "lucide-react";
import { toast } from "sonner";
import { api, useApi, fmtDateTime, fmtTime, errMsg, imgSrc } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Spinner, ErrorBox, Empty, PageHead, JoinCta } from "../components/common";

const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

function MonthView({ events }) {
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const cells = useMemo(() => {
    const first = new Date(cursor);
    const start = new Date(first); start.setDate(1 - first.getDay());
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  }, [cursor]);
  const move = (n) => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + n, 1));
  const same = (a, b) => a.toDateString() === b.toDateString();
  return (
    <div className="rounded-2xl border border-line bg-white p-4" data-testid="calendar-month-view">
      <div className="mb-4 flex items-center justify-between">
        <button onClick={() => move(-1)} className="btn-ghost btn-sm" aria-label="Previous month" data-testid="calendar-prev-btn"><ChevronLeft className="h-4 w-4" /></button>
        <h2 className="text-2xl" aria-live="polite">{cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" })}</h2>
        <button onClick={() => move(1)} className="btn-ghost btn-sm" aria-label="Next month" data-testid="calendar-next-btn"><ChevronRight className="h-4 w-4" /></button>
      </div>
      <div className="grid grid-cols-7 gap-px overflow-hidden rounded-xl bg-line text-xs">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="bg-sand p-2 text-center font-semibold">{d}</div>)}
        {cells.map((d) => {
          const evs = events.filter((e) => same(new Date(e.start), d));
          return (
            <div key={d.toISOString()} className={`min-h-[72px] bg-white p-1.5 sm:min-h-[96px] ${d.getMonth() !== cursor.getMonth() ? "opacity-40" : ""}`}>
              <span className={`text-[11px] ${same(d, new Date()) ? "rounded-full bg-ochre px-1.5 font-bold text-ink" : ""}`}>{d.getDate()}</span>
              {evs.map((e) => (
                <Link key={e.id} to={`/events/${e.slug}`} className="mt-1 block truncate rounded bg-celadon-light px-1 py-0.5 text-[10px] font-medium text-[#2f5a4a] hover:bg-celadon sm:text-[11px]" title={e.title} data-testid={`calendar-event-${e.slug}`}>
                  {e.locked && <Lock className="mr-0.5 inline h-2.5 w-2.5" aria-label="Locked" />}{fmtTime(e.start)} {e.title}
                </Link>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const EventRow = ({ e }) => (
  <Link to={`/events/${e.slug}`} className="kh-card flex flex-col gap-4 overflow-hidden sm:flex-row" data-testid={`event-row-${e.slug}`}>
    <img src={imgSrc(e.cover)} alt="" className="h-40 w-full object-cover sm:h-auto sm:w-48" />
    <div className="flex-1 p-5 sm:pl-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="chip-celadon">{e.type}</span>
        {e.locked && <span className="chip-ochre"><Lock className="h-3 w-3" aria-hidden="true" />{e.access === "invite" ? "Invitation only" : "Members only"}</span>}
        {e.registered && <span className="chip bg-ink text-paper">Registered</span>}
      </div>
      <h3 className="mt-2 text-2xl">{e.title}</h3>
      <p className="mt-1 text-sm text-[#4a4f59]">{fmtDateTime(e.start)} · {e.online ? "Online" : e.location}</p>
    </div>
  </Link>
);

export default function Events() {
  const { data, loading, error, reload } = useApi("/events");
  const [view, setView] = useState("list");
  const nowIso = new Date().toISOString();
  return (
    <div>
      <PageHead eyebrow="Events" title="Workshops, critiques & open studios" intro={`All times are shown in your time zone (${tz}).`}>
        <div role="group" aria-label="Calendar view" className="mt-6 inline-flex rounded-full border border-line bg-white p-1">
          {["list", "month"].map((v) => <button key={v} onClick={() => setView(v)} aria-pressed={view === v} className={`rounded-full px-5 py-2 text-sm font-semibold capitalize ${view === v ? "bg-ink text-paper" : "text-ink"}`} data-testid={`events-view-${v}`}>{v}</button>)}
        </div>
      </PageHead>
      <section className="kh-container">
        {loading ? <Spinner /> : error ? <ErrorBox message={error} onRetry={reload} /> : !data.length ? <Empty title="No events scheduled" /> : view === "month" ? <MonthView events={data} /> : (
          <div className="space-y-10">
            <div><h2 className="mb-4 text-3xl">Upcoming</h2><div className="space-y-4">{data.filter((e) => e.end >= nowIso).map((e) => <EventRow key={e.id} e={e} />)}</div></div>
            <div><h2 className="mb-4 text-3xl">Past events</h2><p className="mb-4 text-sm text-[#4a4f59]">Recordings of past events are available to members.</p><div className="space-y-4">{data.filter((e) => e.end < nowIso).reverse().map((e) => <EventRow key={e.id} e={e} />)}</div></div>
          </div>
        )}
      </section>
    </div>
  );
}

function icsDownload(e) {
  const f = (s) => new Date(s).toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  const body = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Kilnhouse Guild//Demo//EN", "BEGIN:VEVENT", `UID:${e.id}@kilnhouse.demo`, `DTSTAMP:${f(new Date())}`,
    `DTSTART:${f(e.start)}`, `DTEND:${f(e.end)}`, `SUMMARY:${e.title}`, `LOCATION:${e.meeting_link || e.location}`, `DESCRIPTION:${e.title} — Kilnhouse Guild`, "END:VEVENT", "END:VCALENDAR"].join("\r\n");
  const url = URL.createObjectURL(new Blob([body], { type: "text/calendar" }));
  const a = document.createElement("a"); a.href = url; a.download = `${e.slug}.ics`; a.click();
}

export function EventDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const { data: e, loading, error, reload } = useApi(`/events/${slug}`, [user?.id]);
  const [busy, setBusy] = useState(false);
  if (loading) return <Spinner />;
  if (error) return <div className="kh-container py-16"><ErrorBox message={error} /></div>;
  const act = async (method) => {
    setBusy(true);
    try {
      await api[method](`/events/${e.id}/register`);
      toast.success(method === "post" ? "You're registered — a confirmation email is on its way." : "Registration cancelled.");
      reload();
    } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  const full = e.registered_count >= e.capacity;
  return (
    <article className="kh-container max-w-5xl pt-10">
      <Link to="/events" className="text-sm underline decoration-ochre" data-testid="back-to-events">← All events</Link>
      <img src={imgSrc(e.cover)} alt="" className="mt-6 h-64 w-full rounded-[2rem] object-cover sm:h-80" />
      <div className="mt-8 grid gap-10 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="flex flex-wrap gap-2"><span className="chip-celadon">{e.type}</span>{e.locked && <span className="chip-ochre"><Lock className="h-3 w-3" aria-hidden="true" />{e.access === "invite" ? "Invitation only" : "Members only"}</span>}{e.circle && <Link to={`/circles/${e.circle.slug}`} className="chip">{e.circle.name} circle</Link>}</div>
          <h1 className="mt-3 text-4xl sm:text-5xl">{e.title}</h1>
          <div className="prose-kh mt-6" dangerouslySetInnerHTML={{ __html: e.description }} />
          <p className="mt-4 text-sm"><strong>Instructors:</strong> {e.instructors?.join(", ")}</p>
          {e.is_past && (e.recording_url ? (
            <div className="mt-8"><h2 className="mb-3 text-2xl">Recording</h2><video controls src={e.recording_url} className="w-full rounded-2xl bg-black" data-testid="event-recording"><track kind="captions" /></video></div>
          ) : e.has_recording && <div className="mt-8"><JoinCta text="Recordings of past events are for members" /></div>)}
        </div>
        <aside className="kh-card h-fit space-y-4 p-6" data-testid="event-sidebar">
          <p className="text-sm"><strong className="block font-serif text-lg font-normal text-ink">When</strong><span data-testid="event-local-time">{fmtDateTime(e.start)}</span><span className="block text-xs text-[#5a5f69]">Your time zone: {tz}</span></p>
          <p className="flex gap-2 text-sm"><MapPin className="h-4 w-4 shrink-0" aria-hidden="true" />{e.online ? "Online" : e.location}</p>
          <p className="flex gap-2 text-sm" data-testid="event-capacity"><Users className="h-4 w-4" aria-hidden="true" />{e.registered_count} / {e.capacity} registered</p>
          {e.meeting_link && <div className="rounded-xl bg-celadon-light p-3 text-sm" data-testid="event-meeting-link"><Video className="mr-1 inline h-4 w-4" aria-hidden="true" />Meeting link: <a href={e.meeting_link} className="break-all font-medium underline">{e.meeting_link}</a></div>}
          {e.online && !e.meeting_link && <p className="text-xs text-[#5a5f69]">The online meeting link is revealed to registered attendees.</p>}
          {!e.is_past && (!user ? <Link to={`/login?next=/events/${e.slug}`} className="btn-primary w-full" data-testid="event-login-to-register">Log in to register</Link> :
            e.locked ? <div data-testid="event-locked-msg" className="rounded-xl bg-ochre-light p-3 text-sm text-ochre-dark">{e.access === "invite" ? "This preview is by invitation only." : "This event is for paid members."} {e.access !== "invite" && <Link to="/dashboard?tab=billing" className="underline">Upgrade</Link>}</div> :
            e.registered ? <button disabled={busy} onClick={() => act("delete")} className="btn-ghost w-full" data-testid="event-unregister-btn">Cancel registration</button> :
            <button disabled={busy || full} onClick={() => act("post")} className="btn-ochre w-full" data-testid="event-register-btn">{full ? "Event full" : "Register"}</button>)}
          <button onClick={() => icsDownload(e)} className="btn-ghost w-full" data-testid="event-ics-btn"><CalendarPlus className="h-4 w-4" aria-hidden="true" />Add to calendar (.ics)</button>
        </aside>
      </div>
    </article>
  );
}
